import React, { useState } from 'react'
import {
  CButton,
  CFormInput,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react-pro'
import ToastComponent from 'src/components/common/TaostComponent'
import EmailQuotaService from 'src/service/EmailQuotaService'
import './GameNotification.css'

// Three emails an admin sends by hand. They shared a shape already — one accordion and one
// single-row table each — so they are one table here.
const ONE_OFF_EMAILS = [
  {
    key: 'top_managers',
    name: 'Top 10 Managers (Overall)',
    description: "The overall leaderboard's top ten managers and their points.",
    countKey: 'getTopManagerCount',
    send: () => EmailQuotaService.topManagerOverall(),
    sendTest: (email) => EmailQuotaService.testTopManagerOverall({ email }),
  },
  {
    key: 'top_players',
    name: 'Top 10 Players (Overall)',
    description: 'The ten highest-scoring players across the season so far.',
    countKey: 'getTopPlayerCount',
    send: () => EmailQuotaService.topPlayerOveall(),
    sendTest: (email) => EmailQuotaService.testTopPlayerOverall({ email }),
  },
  {
    key: 'final_result',
    name: 'Announce Final Result',
    description: 'Closing email with the final standings and the winner.',
    countKey: 'finalResultCount',
    send: () => EmailQuotaService.getFantasyResult(),
    sendTest: (testEmail) => EmailQuotaService.testAnnountFinalResult({ testEmail }),
  },
]

const OneOffEmails = ({ alertCount, refreshCounts }) => {
  const [testFor, setTestFor] = useState(null)
  const [testEmail, setTestEmail] = useState('')
  const [sending, setSending] = useState(false)

  const [confirmFor, setConfirmFor] = useState(null)
  const [sendingKey, setSendingKey] = useState(null)

  const closeTest = () => {
    setTestFor(null)
    setTestEmail('')
  }

  const handleSendTestEmail = () => {
    if (!testEmail) {
      ToastComponent('Please enter an email address', 'error')
      return
    }
    setSending(true)
    testFor.sendTest(testEmail).then((res) => {
      setSending(false)
      ToastComponent(res.message, res.success ? 'success' : 'error')
      if (res.success) closeTest()
    })
  }

  // Sending goes to every member, so it always asks first — the old page fired on one click.
  const handleSendNow = () => {
    const email = confirmFor
    setConfirmFor(null)
    setSendingKey(email.key)
    email
      .send()
      .then((res) => {
        ToastComponent(res.message, res.success ? 'success' : 'error')
        if (res.success) refreshCounts?.()
      })
      .finally(() => setSendingKey(null))
  }

  return (
    <>
      <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
        <table className="table align-middle mb-0">
          <thead>
            <tr>
              <th style={{ width: '46%' }}>Email</th>
              <th style={{ width: '12%' }}>Sent</th>
              <th style={{ width: '42%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ONE_OFF_EMAILS.map((email) => (
              <tr key={email.key}>
                <td>
                  <span className="gn-email-name">{email.name}</span>
                  <span className="gn-email-desc">{email.description}</span>
                </td>
                <td className="gn-num">{alertCount?.[email.countKey] ?? 0}</td>
                <td>
                  <div className="gn-actions">
                    <CButton
                      color="primary"
                      size="sm"
                      disabled={sendingKey === email.key}
                      onClick={() => setConfirmFor(email)}
                    >
                      {sendingKey === email.key ? 'Sending...' : 'Send now'}
                    </CButton>
                    <CButton
                      color="primary"
                      variant="outline"
                      size="sm"
                      onClick={() => setTestFor(email)}
                    >
                      Test email
                    </CButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CModal visible={!!testFor} onClose={closeTest}>
        <CModalHeader>
          <CModalTitle>Send test email</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="gn-panel-note mb-2">
            Sends a preview of <strong>{testFor?.name}</strong> to one address. It does not count
            against your quota.
          </p>
          <CFormInput
            type="email"
            placeholder="Enter test email address"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            aria-label="Test email address"
          />
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={closeTest}>
            Cancel
          </CButton>
          <CButton color="primary" onClick={handleSendTestEmail} disabled={sending}>
            {sending ? 'Sending...' : 'Send test'}
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal visible={!!confirmFor} onClose={() => setConfirmFor(null)}>
        <CModalHeader>
          <CModalTitle>Send to all members?</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <strong>{confirmFor?.name}</strong> will be emailed to every member of this game right
          now, and each email counts against your quota.
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setConfirmFor(null)}>
            Cancel
          </CButton>
          <CButton color="primary" onClick={handleSendNow}>
            Send now
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default OneOffEmails
